import { useEffect, useRef } from 'react';
import { startWrapUpTimer } from '../config/voiceWrapUp';

type UseVoiceWrapUpNoticeArgs = {
  isConnected: boolean;
  connectedAt: number | null;
  delayMs: number | null;
  noticeText: string;
  sendContextualUpdate: (text: string) => void;
};

// * Arms the wrap-up timer for the lifetime of a connected conversation.
// * All the timing logic lives in startWrapUpTimer; this hook only scopes it
// * to the connection via the effect cleanup. Each SDK connect records a new
// * connectedAt, so a second call re-arms, and a disconnect or unmount stops it.
export const useVoiceWrapUpNotice = ({
  isConnected,
  connectedAt,
  delayMs,
  noticeText,
  sendContextualUpdate,
}: UseVoiceWrapUpNoticeArgs): void => {
  // * Held in a ref so a re-render does not tear down and restart the timer.
  const sendContextualUpdateRef = useRef(sendContextualUpdate);
  sendContextualUpdateRef.current = sendContextualUpdate;

  const noticeTextRef = useRef(noticeText);
  noticeTextRef.current = noticeText;

  useEffect(() => {
    if (!isConnected || connectedAt === null || delayMs === null) {
      return;
    }

    const stop = startWrapUpTimer({
      connectedAt,
      delayMs,
      onFire: () => {
        try {
          sendContextualUpdateRef.current(noticeTextRef.current);
          console.info('[VoicePanel] Sent wrap-up contextual update');
        } catch (sendError) {
          console.warn(
            '[VoicePanel] Wrap-up contextual update failed',
            sendError,
          );
        }
      },
    });

    return stop;
  }, [isConnected, connectedAt, delayMs]);
};
